import type {
	InputHTMLAttributes,
	ReactNode,
	SelectHTMLAttributes,
	TextareaHTMLAttributes,
} from "react";

import { cx } from "@/utils/cx";

const CONTROL_CLASS =
	"border-secondary text-primary rounded-md border bg-transparent px-2 py-1 text-sm";

interface FieldProps {
	children: ReactNode;
	htmlFor: string;
	label: string;
}

/** Label + control, stacked. Every control gets a real label for screen readers. */
export const Field = ({ children, htmlFor, label }: FieldProps) => (
	<label className="flex flex-col gap-1 text-sm" htmlFor={htmlFor}>
		<span className="text-secondary text-xs font-medium">{label}</span>
		{children}
	</label>
);

export const TextInput = ({
	className,
	...props
}: InputHTMLAttributes<HTMLInputElement>) => (
	<input className={cx(CONTROL_CLASS, className)} {...props} />
);

/** Multi-line sibling of `TextInput`, for prose rather than a value. */
export const TextArea = ({
	className,
	...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) => (
	<textarea className={cx(CONTROL_CLASS, className)} {...props} />
);

export const Select = ({
	className,
	...props
}: SelectHTMLAttributes<HTMLSelectElement>) => (
	<select className={cx(CONTROL_CLASS, className)} {...props} />
);

interface ButtonProps extends InputHTMLAttributes<HTMLButtonElement> {
	variant?: "danger" | "primary" | "secondary";
}

export const Button = ({
	className,
	variant = "primary",
	...props
}: ButtonProps & { type?: "button" | "submit" }) => (
	<button
		className={cx(
			"rounded-md px-3 py-1 text-sm font-medium disabled:opacity-50",
			variant === "primary" && "bg-brand-600 text-white",
			variant === "secondary" && "border-secondary text-primary border",
			variant === "danger" && "text-error-primary underline",
			className,
		)}
		{...(props as object)}
	/>
);
